import { Agent } from './agentService';
import api from './api';
import { ChatMessageOutput } from './websocketService';

export interface Conversation {
  id: string;
  title: string;
  agent: Agent;
  create_time: string;
  update_time: string;
}

export interface ConversationsResponse {
  data: Conversation[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

/**
 * Response for conversation events/messages
 */
export interface ConversationEventsResponse {
  conversation: Conversation;
  data: ChatMessageOutput[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

/**
 * Order parameter for fetching messages
 */
export type MessageOrder = 'asc' | 'desc';

/**
 * Fetch all conversations from the Echo backend
 * @param limit - Number of conversations to fetch (default: 100)
 * @param offset - Offset for pagination (default: 0)
 * @returns Promise with conversations response
 */
export const fetchConversations = async (
  limit = 100,
  offset = 0,
  agentId?: string | null,
): Promise<ConversationsResponse> => {
  try {
    const response = await api.get<ConversationsResponse>(
      '/api/v1/chat/conversations',
      {
        params: { limit, offset, ...(agentId ? { 'agent-id': agentId } : {}) },
      },
    );

    return response.data;
  } catch (error) {
    console.error('Failed to fetch conversations:', error);
    throw error;
  }
};

/**
 * Fetch chat messages for a specific conversation
 *
 * Returns only text messages (user and assistant) from the conversation,
 * filtering out function calls and other technical events. Messages are
 * returned in ChatMessageOutput format matching the WebSocket interface.
 *
 * @param conversationId - The conversation identifier
 * @param limit - Maximum number of messages to return (1-100, default: 50)
 * @param offset - Number of messages to skip for pagination (default: 0)
 * @param order - Message ordering: "desc" for newest first (default), "asc" for oldest first
 * @returns Promise with conversation events response
 * @throws {401} If authentication fails
 * @throws {403} If user tries to access another user's conversation
 * @throws {404} If conversation does not exist
 * @throws {500} If an unexpected server error occurs
 */
export const fetchConversationEvents = async (
  conversationId: string,
  limit = 50,
  offset = 0,
  order: MessageOrder = 'desc',
): Promise<ConversationEventsResponse> => {
  try {
    // Validate limit range
    const validLimit = Math.max(1, Math.min(100, limit));

    const response = await api.get<ConversationEventsResponse>(
      `/api/v1/chat/conversations/${conversationId}/events`,
      {
        params: {
          limit: validLimit,
          offset,
          order,
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      `Failed to fetch events for conversation ${conversationId}:`,
      error,
    );
    throw error;
  }
};

/**
 * Search conversations by query string
 *
 * Searches conversation titles matching the query parameter.
 * Returns paginated results in the same format as fetchConversations.
 *
 * @param query - Search query string
 * @param limit - Number of conversations to fetch (default: 20)
 * @param offset - Offset for pagination (default: 0)
 * @returns Promise with conversations response
 */
export const searchConversations = async (
  query: string,
  limit = 20,
  offset = 0,
): Promise<ConversationsResponse> => {
  try {
    const response = await api.get<ConversationsResponse>(
      '/api/v1/chat/conversations',
      {
        params: { q: query, limit, offset },
      },
    );

    return response.data;
  } catch (error) {
    console.error('Failed to search conversations:', error);
    throw error;
  }
};

/**
 * Rename a conversation by updating its title
 *
 * @param conversationId - The conversation identifier
 * @param title - New title (1-255 characters)
 * @returns Promise that resolves with the updated conversation
 * @throws {400} If validation error occurs
 * @throws {401} If authentication fails
 * @throws {404} If conversation does not exist
 * @throws {500} If an unexpected server error occurs
 */
export const renameConversation = async (
  conversationId: string,
  title: string,
): Promise<Conversation> => {
  try {
    const response = await api.patch<Conversation>(
      `/api/v1/chat/conversations/${conversationId}`,
      { title },
    );
    return response.data;
  } catch (error) {
    console.error(`Failed to rename conversation ${conversationId}:`, error);
    throw error;
  }
};

/**
 * Delete a conversation and all its associated events
 *
 * Permanently deletes the specified conversation and all associated events
 * using Google ADK's delete_session method which handles cascade deletion.
 * Users can only delete conversations they own.
 *
 * @param conversationId - The conversation identifier to delete
 * @returns Promise that resolves when the conversation is deleted (204 No Content)
 * @throws {401} If authentication fails
 * @throws {403} If user tries to delete another user's conversation
 * @throws {404} If conversation does not exist
 * @throws {422} If validation error occurs
 * @throws {500} If an unexpected server error occurs
 */
export const deleteConversation = async (
  conversationId: string,
): Promise<void> => {
  try {
    await api.delete(`/api/v1/chat/conversations/${conversationId}`);
  } catch (error) {
    console.error(`Failed to delete conversation ${conversationId}:`, error);
    throw error;
  }
};
